import { useState, useMemo, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Checkbox } from './ui/checkbox';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

import DataTableFilters from './data-table/DataTableFilters';
import DataTablePagination from './data-table/DataTablePagination';
import { getNestedValue, applyFilters, applySort } from './data-table/dataTableUtils';

/**
 * Enhanced DataTable Component with Pagination and Bulk Selection
 *
 * iter276 — internal split: filter chrome → `./data-table/DataTableFilters`;
 * pagination chrome → `./data-table/DataTablePagination`; sort/filter logic
 * → `./data-table/dataTableUtils`. Public props API unchanged.
 *
 * Props:
 * - data: Array of objects
 * - columns: Array of column definitions { key, label, sortable, filterable, filterType, filterOptions, render, className, cellClassName }
 * - visibleColumns: Optional array of column keys to display (for column config feature)
 * - onRowClick: Optional callback for row click
 * - actions: Optional render function for action buttons (row) => JSX
 * - emptyIcon: Optional icon component for empty state
 * - emptyMessage: Optional message for empty state
 * - searchable: Enable global search
 * - searchFields: Array of field keys to search in
 * - pagination: Enable pagination (default: true)
 * - pageSize: Default page size (default: 10)
 * - pageSizeOptions: Array of page size options (default: [10, 25, 50, 100])
 * - serverPagination: Enable server-side pagination
 * - totalCount: Total record count for server-side pagination
 * - onPageChange: Callback for page change (page, pageSize) => void
 * - currentPage: Current page for controlled pagination (1-indexed)
 * - selectable: Enable row selection (default: false)
 * - selectedRows: Array of selected row IDs
 * - onSelectionChange: Callback when selection changes
 * - rowIdKey: Key to use as row ID (default: 'id')
 * - defaultFilters: Initial filter map (also opens the filter panel)
 */
const DataTable = ({
  data = [],
  columns = [],
  visibleColumns,
  onRowClick,
  actions,
  emptyIcon: EmptyIcon,
  emptyMessage = 'No data available',
  searchable = true,
  searchFields = [],
  testIdPrefix = 'table',
  // Pagination props
  pagination = true,
  pageSize: defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  serverPagination = false,
  totalCount,
  onPageChange,
  currentPage: controlledPage,
  // Selection props
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  rowIdKey = 'id',
  // Default filters
  defaultFilters = {},
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(defaultFilters);
  const [showFilters, setShowFilters] = useState(Object.keys(defaultFilters).length > 0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(controlledPage || 1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Sync controlled page
  useEffect(() => {
    if (controlledPage !== undefined) {
      setCurrentPage(controlledPage);
    }
  }, [controlledPage]);

  // Filter columns based on visibleColumns prop and maintain their order
  const displayColumns = useMemo(() => {
    if (!visibleColumns || visibleColumns.length === 0) {
      return columns;
    }
    return visibleColumns
      .map(key => columns.find(col => col.key === key))
      .filter(Boolean);
  }, [columns, visibleColumns]);

  const filterableColumns = displayColumns.filter(col => col.filterable);

  // Filter and sort data
  const filteredData = useMemo(() => {
    const filtered = applyFilters({ data, columns, searchTerm, searchFields, filters });
    return applySort(filtered, sortConfig);
  }, [data, columns, searchTerm, searchFields, filters, sortConfig]);

  // Calculate pagination
  const totalRecords = serverPagination ? (totalCount || 0) : filteredData.length;
  const totalPages = Math.ceil(totalRecords / pageSize);

  // Get paginated data (client-side only)
  const paginatedData = useMemo(() => {
    if (!pagination || serverPagination) {
      return filteredData;
    }
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize, pagination, serverPagination]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (!serverPagination) {
      setCurrentPage(1);
    }
  }, [searchTerm, filters, serverPagination]);

  // Page navigation handlers
  const goToPage = (page) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(newPage);
    if (onPageChange) {
      onPageChange(newPage, pageSize);
    }
  };

  const handlePageSizeChange = (newSize) => {
    const newPageSize = parseInt(newSize);
    setPageSize(newPageSize);
    setCurrentPage(1);
    if (onPageChange) {
      onPageChange(1, newPageSize);
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const activeFilterCount =
    Object.values(filters).filter(v => v && v !== 'all').length + (searchTerm ? 1 : 0);
  const hasActiveFilters = searchTerm || Object.values(filters).some(v => v && v !== 'all');

  // Calculate display range
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  // Sorting
  const handleSort = (key) => {
    if (!displayColumns.find(c => c.key === key)?.sortable) return;
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Selection helpers
  const toggleRowSelection = (rowId) => {
    if (!onSelectionChange) return;
    const newSelection = selectedRows.includes(rowId)
      ? selectedRows.filter(id => id !== rowId)
      : [...selectedRows, rowId];
    onSelectionChange(newSelection);
  };

  const toggleSelectAll = () => {
    if (!onSelectionChange) return;
    const currentPageIds = paginatedData.map(row => row[rowIdKey]);
    const allSelected = currentPageIds.every(id => selectedRows.includes(id));
    if (allSelected) {
      onSelectionChange(selectedRows.filter(id => !currentPageIds.includes(id)));
    } else {
      onSelectionChange([...new Set([...selectedRows, ...currentPageIds])]);
    }
  };

  const isAllSelected =
    paginatedData.length > 0 && paginatedData.every(row => selectedRows.includes(row[rowIdKey]));

  return (
    <div className="space-y-4">
      <DataTableFilters
        searchable={searchable}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterableColumns={filterableColumns}
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        hasActiveFilters={hasActiveFilters}
        clearFilters={clearFilters}
        activeFilterCount={activeFilterCount}
        testIdPrefix={testIdPrefix}
      />

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        {paginatedData.length === 0 ? (
          <div className="p-12 text-center">
            {EmptyIcon && <EmptyIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />}
            <p className="text-slate-500">{emptyMessage}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {selectable && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                  )}
                  {displayColumns.map(col => (
                    <TableHead
                      key={col.key}
                      className={`${col.sortable ? 'cursor-pointer hover:bg-slate-100 select-none' : ''} ${col.className || ''}`}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {col.sortable && (
                          <span className="text-slate-400">
                            {sortConfig.key === col.key ? (
                              sortConfig.direction === 'asc' ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3" />
                            )}
                          </span>
                        )}
                      </div>
                    </TableHead>
                  ))}
                  {actions && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((row, idx) => (
                  <TableRow
                    key={row.id || idx}
                    className={`table-row-hover ${onRowClick ? 'cursor-pointer' : ''} ${selectable && selectedRows.includes(row[rowIdKey]) ? 'bg-blue-50' : ''}`}
                    onClick={() => onRowClick && onRowClick(row)}
                    data-testid={`${testIdPrefix}-row-${row.id || idx}`}
                  >
                    {selectable && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedRows.includes(row[rowIdKey])}
                          onCheckedChange={() => toggleRowSelection(row[rowIdKey])}
                          aria-label={`Select row ${idx + 1}`}
                        />
                      </TableCell>
                    )}
                    {displayColumns.map(col => (
                      <TableCell key={col.key} className={col.cellClassName || ''}>
                        {col.render ? col.render(row) : getNestedValue(row, col.key) || '-'}
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {actions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {pagination && totalRecords > 0 && (
        <DataTablePagination
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          handlePageSizeChange={handlePageSizeChange}
          startRecord={startRecord}
          endRecord={endRecord}
          totalRecords={totalRecords}
          currentPage={currentPage}
          totalPages={totalPages}
          goToPage={goToPage}
          testIdPrefix={testIdPrefix}
        />
      )}
    </div>
  );
};

export default DataTable;
